import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ClipboardList } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Badge, Avatar, EmptyState, statusTone, SearchInput } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const FILTERS = ["all", "New", "In Progress", "Review"];

export default function Referrals() {
  const nav = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const { user } = useAuth();
  const load = useCallback(() => {
    api.get("/referrals").then((r) => setReferrals(r.data));
    api.get("/referrals/drafts").then((r) => setDrafts(r.data)).catch(() => setDrafts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { try { await api.post(`/referrals/${id}/approve`); toast.success("Treatment order approved"); load(); } catch { toast.error("Could not approve order"); } };
  const reject = async (id) => { try { await api.post(`/referrals/${id}/reject`); toast.success("Treatment order rejected"); load(); } catch { toast.error("Could not reject order"); } };
  const deleteDraft = async (id) => { try { await api.delete(`/referrals/drafts/${id}`); toast.success("Draft discarded"); load(); } catch { toast.error("Could not discard draft"); } };

  const shown = referrals.filter((r) =>
    (filter === "all" || r.status === filter) &&
    (!q || r.patient_name.toLowerCase().includes(q.toLowerCase()) || (r.diagnosis_label || "").toLowerCase().includes(q.toLowerCase()))
  );
  const count = (f) => f === "all" ? referrals.length : referrals.filter((r) => r.status === f).length;

  return (
    <>
      <PageHead title="Clinical Treatment Orders" subtitle="Every treatment order & care episode across your practice." />

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`referral-filter-${f.replace(/\s/g, "-")}`} onClick={() => setFilter(f)}
              className={`pq-chip ${filter === f ? "pq-chip-active" : ""}`}>
              {f === "all" ? "All" : f} <span className="opacity-70">{count(f)}</span>
            </button>
          ))}
        </div>
        <SearchInput testid="referral-search" value={q} onChange={setQ} placeholder="Search treatment orders…" />
      </div>

      {drafts.length > 0 && filter === "all" && !q && (
        <Card className="mb-4" data-testid="drafts-card">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <Badge tone="amber">Drafts</Badge>
            <span className="text-[12.5px] text-slate-500">Unfinished treatment orders — continue where you left off.</span>
          </div>
          <div className="overflow-x-auto"><table className="w-full">
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 last:border-0 hover:bg-amber-50/40" data-testid={`draft-row-${d.id}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={d.patient_name} size={34} /><div><div className="font-semibold text-[13.5px]">{d.patient_name}</div><div className="text-[11.5px] text-slate-500">{d.diagnosis_label || "Diagnosis not set"}</div></div></div></td>
                  <td className="px-5 py-3 text-[12.5px] text-slate-500">Saved {new Date(d.updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-5 py-3"><Badge tone="amber">Draft · Step {(d.step || 0) + 1}/4</Badge></td>
                  <td className="px-5 py-3"><div className="flex gap-2 justify-end">
                    <Btn size="sm" variant="primary" data-testid={`draft-continue-${d.id}`} onClick={() => nav(`/referrals/new?draft=${d.id}`)}>Continue</Btn>
                    <Btn size="sm" data-testid={`draft-delete-${d.id}`} onClick={() => deleteDraft(d.id)}>Discard</Btn>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      )}

      <Card>
        {shown.length === 0 ? (
          <EmptyState icon={<ClipboardList size={24} />} title="No treatment orders found"
            subtitle="Create a treatment order to generate an AI care pathway and start a care episode."
            action={<Btn variant="primary" onClick={() => nav("/referrals/new")}><Plus size={16} /> New Treatment Order</Btn>} />
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Patient", "Diagnosis", "Ordered On", "Status", "Next Action", ""].map((h) => (
              <th key={h} className="pq-label px-5 py-3 text-left border-b border-slate-100">{h}</th>))}</tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={r.patient_name} size={34} /><div><div className="font-semibold text-[13.5px]">{r.patient_name}</div><div className="text-[11.5px] text-slate-500">{r.patient_meta}</div></div></div></td>
                  <td className="px-5 py-3 text-[13px]">{r.diagnosis_label}</td>
                  <td className="px-5 py-3 text-[13px] text-slate-500">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                  <td className="px-5 py-3">{r.approval_status === "Pending" ? <Badge tone="amber">Pending approval</Badge> : <Badge tone={statusTone(r.status)}>{r.status}</Badge>}</td>
                  <td className="px-5 py-3"><div className="text-[13px] font-semibold">{r.next_action}</div><div className="text-[11.5px] text-slate-500">📅 {r.next_date}</div></td>
                  <td className="px-5 py-3"><div className="flex gap-2 justify-end">
                    {r.approval_status === "Pending" && user?.role === "doctor" && (<>
                      <Btn size="sm" variant="primary" data-testid={`referral-approve-${r.id}`} onClick={() => approve(r.id)}>Approve</Btn>
                      <Btn size="sm" data-testid={`referral-reject-${r.id}`} onClick={() => reject(r.id)}>Reject</Btn>
                    </>)}
                    <Btn size="sm" data-testid={`referral-view-${r.id}`} onClick={() => nav(`/referrals/${r.id}`)}>View</Btn>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
    </>
  );
}
